import React, { useEffect, useState } from "react";
import { api, fmtINR, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Buildings, Plus, ArrowRight, Lightning, PaperPlaneTilt, Calendar, ArrowClockwise, X } from "@phosphor-icons/react";

const STATES = ["Andhra Pradesh","Assam","Bihar","Delhi","Goa","Gujarat","Haryana","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","West Bengal"];

const currentMonth = () => new Date().toISOString().slice(0, 7);
const prevMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

export default function Agency() {
  const { refreshCompany } = useAuth();
  const [data, setData] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    name: "", gstin: "", state: "Maharashtra", address: "",
    phone: "", email: "", pan: "", einvoice_enabled: true,
  });

  // Bulk actions state
  const [bulkMonth, setBulkMonth] = useState(prevMonth());
  const [returnType, setReturnType] = useState("GSTR1");
  const [selected, setSelected] = useState(new Set());
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async () => {
    const [ov, cal] = await Promise.all([
      api.get("/agency/overview"),
      api.get("/agency/compliance-calendar"),
    ]);
    setData(ov.data);
    setCalendar(cal.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === data.companies.length) setSelected(new Set());
    else setSelected(new Set(data.companies.map((c) => c.id)));
  };

  const switchTo = async (id, name) => {
    try {
      await api.post(`/agency/switch/${id}`);
      await refreshCompany();
      toast.success(`Switched to ${name}`);
      window.location.href = "/app";
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", form);
      toast.success(`${form.name} added to portfolio`);
      setShow(false);
      setForm({ name: "", gstin: "", state: "Maharashtra", address: "", phone: "", email: "", pan: "", einvoice_enabled: true });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const bulkPreview = async () => {
    setBulkLoading(true);
    try {
      const { data } = await api.post("/agency/bulk/gstr-preview", {
        month: bulkMonth, return_type: returnType,
        company_ids: selected.size ? Array.from(selected) : null,
      });
      setBulkResult({ kind: "preview", data });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setBulkLoading(false);
  };

  const bulkFile = async () => {
    if (!confirm(`File ${returnType} for ${selected.size || "all"} clients for ${bulkMonth}?`)) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/agency/bulk/file-gstr", {
        month: bulkMonth, return_type: returnType,
        company_ids: selected.size ? Array.from(selected) : null,
      });
      toast.success(`Filed for ${data.filed_count} clients (${data.skipped_count} skipped)`);
      setBulkResult({ kind: "filed", data });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setBulkLoading(false);
  };

  const bulkRecurring = async () => {
    setBulkLoading(true);
    try {
      const { data } = await api.post("/agency/bulk/run-recurring");
      toast.success(`Recurring: generated ${data.generated} of ${data.checked}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setBulkLoading(false);
  };

  if (!data) return <div className="text-neutral-500">Loading portfolio…</div>;

  const t = data.totals;

  return (
    <div className="space-y-6 lb-fade" data-testid="agency-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="lb-section-head">Partner Portal</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Agency Portfolio</h1>
          <p className="text-neutral-600 text-sm mt-1">
            Managing <b>{data.companies.length}</b> {data.companies.length === 1 ? "client" : "clients"} · aggregate view across your entire book.
          </p>
        </div>
        <button className="lb-btn" onClick={() => setShow((s) => !s)} data-testid="add-client-btn">
          <Plus size={14} /> Add Client Company
        </button>
      </div>

      {/* Aggregate KPIs — color coded */}
      <div className="grid grid-cols-5 gap-3">
        <div className="lb-kpi lb-kpi-count">
          <div className="lb-kpi-label">Clients</div>
          <div className="lb-kpi-value">{t.company_count}</div>
        </div>
        <div className="lb-kpi lb-kpi-revenue">
          <div className="lb-kpi-label">Total Revenue</div>
          <div className="lb-kpi-value mono pos">{fmtINR(t.revenue)}</div>
        </div>
        <div className="lb-kpi lb-kpi-outstanding">
          <div className="lb-kpi-label">Outstanding</div>
          <div className="lb-kpi-value mono neg">{fmtINR(t.outstanding)}</div>
        </div>
        <div className="lb-kpi lb-kpi-gst">
          <div className="lb-kpi-label">GST Payable</div>
          <div className="lb-kpi-value mono">{fmtINR(t.gst_payable)}</div>
        </div>
        <div className="lb-kpi lb-kpi-cash">
          <div className="lb-kpi-label">Cash Position</div>
          <div className="lb-kpi-value mono">{fmtINR(t.cash_position)}</div>
        </div>
      </div>

      {/* Compliance calendar strip */}
      {calendar.length > 0 && (
        <div className="lb-panel-accent p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-display font-bold">
              <Calendar size={16} /> Compliance calendar — {calendar.length} item{calendar.length !== 1 ? "s" : ""} need attention
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            {calendar.slice(0, 6).map((c, i) => (
              <div key={i} className="bg-white border border-amber-200 p-2.5" data-testid={`calendar-item-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold">{c.company_name}</span>
                  <span className={"lb-badge " + (
                    c.status === "overdue" ? "lb-badge-danger" :
                    c.status === "action_needed" ? "lb-badge-warning" : "lb-badge-info"
                  )}>{c.type}</span>
                </div>
                <div className="text-[11px] text-neutral-600 mt-1 font-mono">
                  {c.month && `${c.month} · `}
                  {c.days_left !== undefined && (c.days_left > 0 ? `${c.days_left} days left` : "OVERDUE")}
                  {c.count !== undefined && `${c.count} invoice${c.count !== 1 ? "s" : ""}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk actions bar */}
      <div className="lb-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Lightning size={16} weight="fill" className="text-amber-500" />
          <div className="font-display font-bold">Bulk actions</div>
          <div className="text-xs text-neutral-500">
            {selected.size ? `${selected.size} selected` : `applies to all ${data.companies.length} clients`}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="lb-label">Period</label>
            <input type="month" className="lb-input w-40" value={bulkMonth}
                   onChange={(e) => setBulkMonth(e.target.value)} data-testid="bulk-month" />
          </div>
          <div>
            <label className="lb-label">Return</label>
            <select className="lb-select w-32" value={returnType}
                    onChange={(e) => setReturnType(e.target.value)} data-testid="bulk-return-type">
              <option value="GSTR1">GSTR-1</option>
              <option value="GSTR3B">GSTR-3B</option>
            </select>
          </div>
          <button onClick={bulkPreview} disabled={bulkLoading} className="lb-btn lb-btn-ghost" data-testid="bulk-preview-btn">
            <ArrowClockwise size={14} /> Preview
          </button>
          <button onClick={bulkFile} disabled={bulkLoading} className="lb-btn lb-btn-accent" data-testid="bulk-file-btn">
            <PaperPlaneTilt size={14} /> File {returnType}
          </button>
          <div className="border-l border-neutral-300 h-8 mx-2"></div>
          <button onClick={bulkRecurring} disabled={bulkLoading} className="lb-btn lb-btn-ghost" data-testid="bulk-recurring-btn">
            <Lightning size={14} /> Run recurring · all clients
          </button>
        </div>
      </div>

      {show && (
        <form onSubmit={submit} className="lb-card p-6 grid grid-cols-3 gap-4" data-testid="client-form">
          <div className="col-span-2">
            <label className="lb-label">Client Legal Name</label>
            <input required className="lb-input" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cli-name" />
          </div>
          <div>
            <label className="lb-label">GSTIN</label>
            <input className="lb-input font-mono uppercase" maxLength={15} value={form.gstin}
                   onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} data-testid="cli-gstin" />
          </div>
          <div>
            <label className="lb-label">State</label>
            <select required className="lb-select" value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}>
              {STATES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="lb-label">PAN</label>
            <input className="lb-input font-mono uppercase" maxLength={10} value={form.pan}
                   onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <label className="lb-label">Phone</label>
            <input className="lb-input" value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="lb-label">Email</label>
            <input type="email" className="lb-input" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">Address</label>
            <input className="lb-input" value={form.address}
                   onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="col-span-3 flex gap-2">
            <button className="lb-btn" data-testid="cli-submit">Add to Portfolio</button>
            <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShow(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Portfolio table */}
      <div className="lb-card">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="font-display font-bold text-lg">Clients under management</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
            select rows for bulk actions · click name to switch
          </div>
        </div>
        <table className="lb-table">
          <thead>
            <tr>
              <th style={{width: 30}}>
                <input type="checkbox" onChange={toggleAll}
                       checked={selected.size === data.companies.length && data.companies.length > 0}
                       data-testid="select-all" />
              </th>
              <th>Company</th>
              <th>GSTIN</th>
              <th>State</th>
              <th>Role</th>
              <th className="text-right">Revenue</th>
              <th className="text-right">Outstanding</th>
              <th className="text-right">GST</th>
              <th className="text-right">Inv.</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.companies.map((c) => (
              <tr key={c.id} data-testid={`agency-row-${c.name}`}>
                <td>
                  <input type="checkbox" checked={selected.has(c.id)}
                         onChange={() => toggle(c.id)}
                         data-testid={`select-${c.id}`} />
                </td>
                <td>
                  <button onClick={() => switchTo(c.id, c.name)}
                          className="flex items-center gap-2 hover:underline text-left"
                          data-testid={`switch-${c.name}`}>
                    <Buildings size={16} className="text-neutral-400" />
                    <span className="font-semibold">{c.name}</span>
                  </button>
                </td>
                <td className="font-mono text-xs">{c.gstin || "—"}</td>
                <td className="text-sm">{c.state}</td>
                <td>
                  <span className={"lb-badge " + (
                    c.role === "owner" ? "lb-badge-dark" :
                    c.role === "accountant" ? "lb-badge-info" : ""
                  )}>{c.role}</span>
                </td>
                <td className="amt">{fmtINR(c.revenue)}</td>
                <td className={"amt " + (c.outstanding > 0 ? "text-rose-600" : "")}>{fmtINR(c.outstanding)}</td>
                <td className="amt">{fmtINR(c.gst_payable)}</td>
                <td className="num">{c.invoice_count}</td>
                <td>
                  <button onClick={() => switchTo(c.id, c.name)} className="text-neutral-400 hover:text-[#0A66C2]">
                    <ArrowRight size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!data.companies.length && (
              <tr><td colSpan={10} className="text-center py-10 text-neutral-500">
                No clients yet. Click <b>Add Client Company</b> to onboard your first.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Bulk results modal */}
      {bulkResult && (
        <div className="fixed inset-0 z-40 bg-black/50 grid place-items-center p-4" onClick={() => setBulkResult(null)}>
          <div className="lb-card p-5 w-full max-w-4xl bg-white max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display font-bold text-xl">
                  {bulkResult.kind === "preview" ? `Preview · ${bulkResult.data.return_type}` : `Filed · ${bulkResult.data.return_type}`}
                </div>
                <div className="text-sm text-neutral-500">Period {bulkResult.data.month || bulkMonth}</div>
              </div>
              <button onClick={() => setBulkResult(null)} className="text-neutral-500 hover:text-[#0A66C2]">
                <X size={20} />
              </button>
            </div>

            {bulkResult.kind === "preview" && bulkResult.data.totals && (
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[
                  { l: "Clients", v: `${bulkResult.data.totals.with_data} / ${bulkResult.data.totals.companies}` },
                  { l: "Taxable", v: fmtINR(bulkResult.data.totals.taxable) },
                  { l: "Net Tax", v: fmtINR(bulkResult.data.totals.net_tax) },
                  { l: "Total", v: fmtINR(bulkResult.data.totals.total) },
                ].map((k) => (
                  <div key={k.l} className="border border-neutral-200 p-2">
                    <div className="text-[10px] uppercase text-neutral-500 font-semibold">{k.l}</div>
                    <div className="font-mono font-bold mt-1">{k.v}</div>
                  </div>
                ))}
              </div>
            )}

            <table className="lb-table">
              <thead>
                <tr>
                  <th>Company</th><th>GSTIN</th>
                  {bulkResult.kind === "preview" ? (
                    <>
                      <th className="text-right">Invoices</th>
                      <th className="text-right">Taxable</th>
                      <th className="text-right">Net Tax</th>
                      <th className="text-right">Total</th>
                    </>
                  ) : (
                    <>
                      <th>Status</th>
                      <th>ARN</th>
                      <th className="text-right">Invoices</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {bulkResult.data.results.map((r) => (
                  <tr key={r.company_id}>
                    <td className="font-semibold">{r.company_name}</td>
                    <td className="font-mono text-xs">{r.gstin || "—"}</td>
                    {bulkResult.kind === "preview" ? (
                      <>
                        <td className="num">{r.invoice_count}</td>
                        <td className="amt">{fmtINR(r.taxable)}</td>
                        <td className="amt">{fmtINR(r.net_tax)}</td>
                        <td className="amt font-semibold">{fmtINR(r.total)}</td>
                      </>
                    ) : (
                      <>
                        <td>
                          <span className={"lb-badge " + (r.status === "filed" ? "lb-badge-success" : "")}>{r.status}</span>
                        </td>
                        <td className="font-mono text-xs">{r.arn || r.reason || "—"}</td>
                        <td className="num">{r.invoice_count || 0}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
